import React, { useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { RightPanel } from './RightPanel'
import { CopilotChat } from '@/components/copilot/CopilotChat'
import './TeamsShell.css'

/**
 * The Teams tab shell. Deliberately has none of the web shell's heavy
 * chrome (logo, app switcher, search bar, notifications bell, settings
 * menu, breadcrumb, status bar) — Teams already renders its own app frame
 * and identity chrome around this iframe. But the manifest exposes a
 * single tab, so without any navigation of its own the rest of the product
 * (Hoy, Personas, Usuarios…) would be unreachable inside Teams: a compact,
 * token-themed nav row is the minimum chrome this host genuinely needs.
 *
 * Copilot Sales (MASTER.md AA-16) reuses the exact same RightPanel +
 * CopilotChat + useCopilotChat + POST /api/copilot/chat as the web shell
 * (src/components/layout/AppLayout.tsx) — no second implementation, same
 * auth, same tools, same endpoint. Only the entry point (a nav button here,
 * a top-bar slot on web) and the surrounding layout differ, because this
 * shell has no web-style header/grid at all to reuse.
 */
const PRIMARY_NAV_ITEMS = [
  { label: 'Hoy', path: '/hoy' },
  { label: 'Empresas', path: '/relaciones' },
  { label: 'Personas', path: '/personas' },
]

const SECONDARY_NAV_ITEMS = [{ label: 'Configuración', path: '/configuracion' }]

export const TeamsShell: React.FC = () => {
  const currentUser = useCurrentUser()
  const isAdmin = currentUser.data?.user.rol === 'administrador'
  const secondaryItems = isAdmin
    ? [...SECONDARY_NAV_ITEMS, { label: 'Usuarios', path: '/usuarios' }]
    : SECONDARY_NAV_ITEMS
  const [copilotOpen, setCopilotOpen] = useState(false)

  const renderItem = (item: { label: string; path: string }) => (
    <NavLink
      key={item.path}
      to={item.path}
      className={({ isActive }) => `teamsShell__navItem ${isActive ? 'teamsShell__navItem--active' : ''}`}
    >
      {item.label}
    </NavLink>
  )

  return (
    <div className="teamsShell">
      <nav className="teamsShell__nav" aria-label="Secciones de Wacner Sales">
        <div className="teamsShell__navGroup">{PRIMARY_NAV_ITEMS.map(renderItem)}</div>
        <div className="teamsShell__navGroup teamsShell__navGroup--secondary">
          {secondaryItems.map(renderItem)}
          <button
            type="button"
            className="teamsShell__copilotBtn"
            aria-pressed={copilotOpen}
            aria-label="Copilot Sales"
            title="Copilot Sales"
            onClick={() => setCopilotOpen((value) => !value)}
          >
            <span aria-hidden="true">✨</span>
            <span>Copilot</span>
          </button>
        </div>
      </nav>
      <div className="teamsShell__body">
        <div className="teamsShell__page">
          <Outlet />
        </div>
        <RightPanel isOpen={copilotOpen} title="Copilot Sales" onClose={() => setCopilotOpen(false)}>
          <CopilotChat />
        </RightPanel>
      </div>
    </div>
  )
}
